import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { Button } from '../ui/button';
import { stepsData } from '../../data/mock';
import { usePodPanel, usePreApproval } from '../../pages/LandingPage';
import {
  ClipboardCheck,
  CreditCard,
  Calendar,
  Phone,
  FileCheck,
  Percent,
  Zap,
  Users,
  LayoutDashboard,
  Send,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  MessageCircle,
  Play,
  Rocket,
  Lock,
  Clock,
  Mail,
  Globe
} from 'lucide-react';

const iconMap = {
  ClipboardCheck,
  CreditCard,
  Calendar,
  Phone,
  FileCheck,
  Percent,
  Zap,
  Users,
  LayoutDashboard,
  Send
};

// Steps that require $75 application fee payment
const LOCKED_STEPS = [6, 7, 8, 9, 10];

const StepDetails = () => {
  const { openPodPanel } = usePodPanel();
  const { openPreApproval } = usePreApproval();
  const [hasApplicationFeePaid, setHasApplicationFeePaid] = useState(false);
  
  // API URL for checkout
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Check if application fee has been paid
  useEffect(() => {
    const checkPaymentStatus = () => {
      // Check localStorage for payment status
      const paymentStatus = localStorage.getItem('applicationFeePaid');
      if (paymentStatus === 'true') {
        setHasApplicationFeePaid(true);
        return;
      }
      
      // Check URL params (in case user just paid)
      const urlParams = new URLSearchParams(window.location.search);
      const paymentSuccess = urlParams.get('payment_success');
      const packageId = urlParams.get('package');
      
      if (paymentSuccess === 'true' && packageId === 'application_fee') {
        localStorage.setItem('applicationFeePaid', 'true');
        setHasApplicationFeePaid(true);
      }
    };
    
    checkPaymentStatus();
    
    // Listen for storage changes (in case payment happens in another tab)
    window.addEventListener('storage', checkPaymentStatus);
    return () => window.removeEventListener('storage', checkPaymentStatus);
  }, []);
  
  // Function to initiate checkout
  const initiateCheckout = async (packageId) => {
    // Save minimal data for checkout
    localStorage.setItem('checkoutSource', 'step_details');
    
    // GHL Payment Links for each funding option
    const paymentLinks = {
      'funding_4_percent': 'https://link.oneforcelive.io/payment-link/690d0418ac56feeb9ffab328',
      'funding_5_percent': 'https://link.oneforcelive.io/payment-link/6995091344f21fe74bd5f960',
      'funding_6_percent': 'https://link.oneforcelive.io/payment-link/699507b4e6b911473ce8c546'
    };
    
    const paymentUrl = paymentLinks[packageId];
    if (paymentUrl) {
      // Mark that we're going to funding payment
      localStorage.setItem('pendingFundingPayment', 'true');
      window.location.href = paymentUrl;
    }
  };
  
  // Check if step is locked
  const isStepLocked = (stepId) => {
    return LOCKED_STEPS.includes(stepId) && !hasApplicationFeePaid;
  };
  
  // Scroll to pre-approval section
  const scrollToPreApproval = () => {
    const fundingSection = document.getElementById('funding-assistant');
    if (fundingSection) {
      fundingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  return (
    <section id="steps" className="py-20 md:py-32 bg-slate-950 relative">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-cyan-500/5 to-transparent" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Step-by-Step Path Details
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Click on any step to see exactly what happens, what questions are asked, and how to proceed.
          </p>
          {!hasApplicationFeePaid && (
            <p className="text-emerald-400 text-sm mt-4">
              <Lock className="w-4 h-4 inline mr-1" />
              Steps 6-10 are locked until you complete the pre-approval process and pay the $75 application fee.
            </p>
          )}
        </div>

        {/* Steps Accordion */}
        <Accordion type="single" collapsible className="space-y-4">
          {stepsData.map((step) => {
            const Icon = iconMap[step.icon];
            const locked = isStepLocked(step.id);

            return (
              <AccordionItem
                key={step.id}
                value={`step-${step.id}`}
                className={`bg-slate-900/50 backdrop-blur-sm border rounded-xl overflow-hidden transition-colors ${
                  locked 
                    ? 'border-slate-700 opacity-75' 
                    : 'border-slate-800 data-[state=open]:border-emerald-500/50'
                }`}
              >
                <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-slate-800/30 transition-colors [&[data-state=open]>div>.step-icon]:bg-emerald-500 [&[data-state=open]>div>.step-icon]:text-white">
                  <div className="flex items-center space-x-4 text-left w-full">
                    <div className={`step-icon w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                      locked ? 'bg-slate-700' : 'bg-slate-800'
                    }`}>
                      {locked ? (
                        <Lock className="w-6 h-6 text-slate-500" />
                      ) : (
                        <Icon className="w-6 h-6 text-emerald-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <span className={`text-sm font-medium ${locked ? 'text-slate-500' : 'text-emerald-400'}`}>
                        Step {step.id}
                      </span>
                      <h3 className={`font-semibold text-lg ${locked ? 'text-slate-400' : 'text-white'}`}>
                        {step.title}
                      </h3>
                    </div>
                    {locked && (
                      <span className="text-xs text-slate-500 bg-slate-800 px-2 py-1 rounded-full">
                        Locked
                      </span>
                    )}
                  </div>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  {locked ? (
                    /* Locked Content Message */
                    <div className="pt-4 border-t border-slate-800">
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 text-center">
                        <Lock className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                        <h4 className="text-white text-lg font-semibold mb-2">
                          Complete Pre-Approval First
                        </h4>
                        <p className="text-slate-300 mb-4">
                          Steps 6-10 are available after you complete the pre-approval process and pay the $75 application fee.
                        </p>
                        <div className="space-y-3">
                          <Button 
                            onClick={openPreApproval}
                            className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-8 py-3 rounded-xl transition-all hover:shadow-lg hover:shadow-emerald-500/25"
                          >
                            Start Pre-Approval Process
                          </Button>
                          <p className="text-slate-500 text-sm">
                            or{' '}
                            <button 
                              onClick={scrollToPreApproval}
                              className="text-emerald-400 hover:text-amber-300 underline"
                            >
                              scroll to pre-approval section
                            </button>
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Normal Unlocked Content */
                    <div className="pt-4 border-t border-slate-800">
                      {/* Purpose */}
                      <div className="mb-6">
                        <h4 className="text-slate-400 text-sm font-medium mb-2">PURPOSE</h4>
                        <p className="text-white">{step.purpose}</p>
                      </div>

                      {/* Description */}
                      <div className="mb-6">
                        <h4 className="text-slate-400 text-sm font-medium mb-2">WHAT HAPPENS</h4>
                        <p className="text-slate-300">{step.description}</p>
                      </div>

                      {/* Questions Asked (if available) */}
                      {step.questionsAsked && (
                        <div className="mb-6">
                          <h4 className="text-slate-400 text-sm font-medium mb-3">QUESTIONS ASKED</h4>
                          <ul className="space-y-2">
                            {step.questionsAsked.map((question, idx) => (
                              <li key={idx} className="flex items-start space-x-3">
                                <MessageCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                                <span className="text-slate-300">{question}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Important Notes (if available) */}
                      {step.importantNotes && (
                        <div className="mb-6 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                          <h4 className="text-emerald-400 text-sm font-medium mb-3 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-2" />
                            IMPORTANT NOTES
                          </h4>
                          <ul className="space-y-2">
                            {step.importantNotes.map((note, idx) => (
                              <li key={idx} className="flex items-center space-x-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                                <span className="text-slate-300">{note}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Curiosity Question (Step 4) */}
                      {step.curiosityQuestion && (
                        <div className="mb-6 bg-slate-800/50 rounded-lg p-4">
                          <h4 className="text-slate-400 text-sm font-medium mb-2">ONE CURIOSITY QUESTION</h4>
                          <p className="text-white italic">&quot;{step.curiosityQuestion}&quot;</p>
                        </div>
                      )}
                      
                      {/* Step 4 specific note */}
                      {step.id === 4 && (
                        <div className="mb-6 bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4">
                          <p className="text-cyan-400 text-sm">
                            <AlertCircle className="w-4 h-4 inline mr-2" />
                            Pre-Call will happen only if you booked a call. Ignore if you did this on your own.
                          </p>
                        </div>
                      )}

                      {/* Closing Flow (Step 5) */}
                      {step.closingFlow && (
                        <div className="mb-6">
                          <h4 className="text-slate-400 text-sm font-medium mb-3">8-STEP CLOSING FLOW</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {step.closingFlow.map((flowStep, idx) => (
                              <div
                                key={idx}
                                className="bg-slate-800/50 rounded-lg p-3 text-center"
                              >
                                <span className="text-emerald-400 text-xs font-semibold block mb-1">
                                  {idx + 1}
                                </span>
                                <span className="text-slate-300 text-sm">{flowStep}</span>
                              </div>
                            ))}
                          </div>
                          {step.note && (
                            <p className="mt-3 text-emerald-400 text-sm">
                              <CheckCircle2 className="w-4 h-4 inline mr-1" />
                              {step.note}
                            </p>
                          )}
                          
                          {/* Step 5: Explore Resources - Website, Directory, POD */}
                          <div className="mt-6 space-y-3">
                            {/* Website Button */}
                            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                              <p className="text-slate-300 text-sm mb-3">
                                Explore the AI Horse-Powered Systems™ website:
                              </p>
                              <a 
                                href="https://aihpower.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-2 rounded-lg transition-all hover:shadow-lg hover:shadow-emerald-500/25 group"
                              >
                                Explore aihpower.com
                                <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                              </a>
                            </div>
                            
                            {/* Directory Button */}
                            <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                              <p className="text-slate-300 text-sm mb-3">
                                Discover the Best of Best Directory — list once, get seen everywhere:
                              </p>
                              <a 
                                href="https://search.aihorsepowered.com/how-it-works"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center bg-cyan-500 hover:bg-cyan-400 text-white font-semibold px-6 py-2 rounded-lg transition-all hover:shadow-lg hover:shadow-cyan-500/25 group"
                              >
                                Explore AI Horse-Powered Directory
                                <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                              </a>
                            </div>
                            
                            {/* POD Button */}
                            <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                              <p className="text-slate-300 text-sm mb-3">
                                Preview the AI Horse-Powered POD™ platform you&apos;ll have access to:
                              </p>
                              <Button 
                                onClick={openPodPanel}
                                className="bg-purple-500 hover:bg-purple-400 text-white font-semibold px-6 py-2 rounded-lg transition-all hover:shadow-lg hover:shadow-purple-500/25 group"
                              >
                                Explore the POD Platform
                                <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Funding Options (Step 6) - Enhanced with checkout links */}
                      {step.options && (
                        <div className="mb-6">
                          <h4 className="text-slate-400 text-sm font-medium mb-3">FUNDING OPTIONS</h4>
                          <div className="space-y-4">
                            {/* 4% Option - Most Popular */}
                            <button
                              onClick={() => initiateCheckout('funding_4_percent')}
                              className="w-full bg-slate-800 hover:bg-slate-700 border-2 border-green-500/50 hover:border-green-400 rounded-xl p-5 text-left transition-all group relative"
                            >
                              <div className="absolute -top-2 -right-2 bg-emerald-500 text-black text-xs font-bold px-2 py-1 rounded-full">
                                MOST POPULAR
                              </div>
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-3xl font-bold text-white group-hover:text-green-400 transition-colors">4%</span>
                                  <span className="text-emerald-400 text-sm font-medium ml-2">= $4,800 One-Time</span>
                                </div>
                                <span className="text-green-400 text-sm">Best Value - Save $2,400!</span>
                              </div>
                              <p className="text-slate-400 text-sm mt-2">Funding Fee Upfront</p>
                            </button>
                            
                            {/* 5% Option - 3 Month Plan */}
                            <button
                              onClick={() => initiateCheckout('funding_5_percent')}
                              className="w-full bg-slate-800 hover:bg-slate-700 border-2 border-slate-700 hover:border-cyan-500/50 rounded-xl p-5 text-left transition-all group"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-3xl font-bold text-white group-hover:text-cyan-400 transition-colors">5%</span>
                                  <span className="text-emerald-400 text-sm font-medium ml-2">= $2,000/mo × 3</span>
                                </div>
                                <span className="text-slate-400 text-sm">$6,000 total</span>
                              </div>
                              <p className="text-slate-400 text-sm mt-2">3 Month Easy Payment Plan</p>
                            </button>
                            
                            {/* 6% Option - 12 Month Plan (Link style) */}
                            <div className="text-center pt-2">
                              <button
                                onClick={() => initiateCheckout('funding_6_percent')}
                                className="text-slate-400 hover:text-emerald-400 text-sm underline transition-colors"
                              >
                                Need other Easy Payment Options? Choose 12 month Easy Payment Plan for 6% Funding Fee ($600/mo × 12 = $7,200)
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* What Happens (Step 7) - Enhanced with POD Trial */}
                      {step.whatHappens && (
                        <div className="mb-6">
                          <h4 className="text-slate-400 text-sm font-medium mb-3">WHAT HAPPENS</h4>
                          <ul className="space-y-2">
                            {step.whatHappens.map((item, idx) => (
                              <li key={idx} className="flex items-center space-x-3">
                                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                                <span className="text-slate-300">{item}</span>
                              </li>
                            ))}
                          </ul>
                          
                          {/* Step 7: View Pricing CTA */}
                          {step.id === 7 && (
                            <div className="mt-6 p-4 bg-gradient-to-r from-cyan-500/20 to-green-500/20 border border-cyan-500/30 rounded-lg">
                              <div className="flex items-center gap-2 mb-3">
                                <Rocket className="w-5 h-5 text-cyan-400" />
                                <h4 className="text-white font-semibold">View Pricing Details</h4>
                              </div>
                              <p className="text-slate-300 text-sm mb-4">
                                Your $120K Funding is active! Click below to view pricing and pick your plan:
                              </p>
                              <a 
                                href="https://aihpower.com/pricing"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full inline-flex items-center justify-center bg-gradient-to-r from-cyan-500 to-green-500 hover:from-cyan-400 hover:to-green-400 text-white font-bold px-6 py-4 rounded-xl transition-all hover:shadow-lg hover:shadow-cyan-500/25 group"
                              >
                                <Rocket className="mr-2 w-5 h-5" />
                                View Pricing on aihpower.com
                                <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                              </a>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Features (Step 9) */}
                      {step.features && (
                        <div className="mb-6">
                          <h4 className="text-slate-400 text-sm font-medium mb-3">PLATFORM FEATURES</h4>
                          <div className="grid grid-cols-2 gap-3">
                            {step.features.map((feature, idx) => (
                              <div
                                key={idx}
                                className="flex items-center space-x-2 bg-slate-800/50 rounded-lg p-3"
                              >
                                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                                <span className="text-slate-300 text-sm">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Benefits (Step 10) */}
                      {step.benefits && (
                        <div className="mb-6">
                          <h4 className="text-slate-400 text-sm font-medium mb-3">BENEFITS</h4>
                          <ul className="space-y-2">
                            {step.benefits.map((benefit, idx) => (
                              <li key={idx} className="flex items-center space-x-3">
                                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                                <span className="text-slate-300">{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Step 1: Get Qualified Button */}
                      {step.id === 1 && (
                        <Button 
                          onClick={openPreApproval}
                          data-testid={`step-${step.id}-cta`}
                          className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-8 py-3 rounded-xl transition-all hover:shadow-lg hover:shadow-emerald-500/25 group"
                        >
                          {step.ctaText}
                          <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </Button>
                      )}
                      
                      {/* Step 2: Application Fee - Opens Pre-Approval Wizard */}
                      {step.id === 2 && (
                        <div className="space-y-3">
                          <Button 
                            onClick={openPreApproval}
                            data-testid={`step-${step.id}-cta`}
                            className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-8 py-3 rounded-xl transition-all hover:shadow-lg hover:shadow-emerald-500/25 group"
                          >
                            Pay $75 Application Fee
                            <CreditCard className="ml-2 w-4 h-4" />
                          </Button>
                          <p className="text-slate-500 text-sm">
                            Complete Step 1 first, then pay your application fee to continue.
                          </p>
                        </div>
                      )}
                      
                      {/* Step 3: Book Call with Skip Option */}
                      {step.id === 3 && (
                        <div className="space-y-4">
                          <a
                            href={step.link}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-8 py-3 rounded-xl transition-all hover:shadow-lg hover:shadow-emerald-500/25 group">
                              {step.ctaText}
                              <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </Button>
                          </a>
                          <div className="text-center">
                            <button 
                              onClick={() => {}}
                              className="text-slate-500 hover:text-slate-400 text-sm underline transition-colors"
                            >
                              Or skip and compete on your own
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Step 9: Login Info */}
                      {step.id === 9 && (
                        <div className="space-y-4">
                          <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                              <Mail className="w-5 h-5 text-cyan-400" />
                              <h4 className="text-white font-semibold">Login Instructions Coming Soon!</h4>
                            </div>
                            <div className="space-y-2 mb-4">
                              <div className="flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                                <p className="text-slate-300 text-sm">Login credentials will be sent to your email</p>
                              </div>
                              <div className="flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                                <p className="text-slate-300 text-sm">Check your inbox and spam folder</p>
                              </div>
                            </div>
                            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg mb-4">
                              <p className="text-slate-300 text-sm">
                                <span className="text-emerald-400 font-semibold">Tip:</span> Visit our website and go to the <span className="font-semibold text-white">"Access"</span> menu to find your platform login links.
                              </p>
                            </div>
                            <a 
                              href="https://aihpower.com"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full inline-flex items-center justify-center bg-cyan-500 hover:bg-cyan-400 text-white font-semibold px-6 py-3 rounded-xl transition-all hover:shadow-lg hover:shadow-cyan-500/25 group"
                            >
                              <Globe className="mr-2 w-5 h-5" />
                              Visit aihpower.com
                              <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </a>
                          </div>
                        </div>
                      )}
                      
                      {/* Step 10: Accept Invites */}
                      {step.id === 10 && (
                        <div className="space-y-4">
                          <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                              <Users className="w-5 h-5 text-emerald-400" />
                              <h4 className="text-white font-semibold">Accept All Platform Invites</h4>
                            </div>
                            <p className="text-slate-300 text-sm mb-4">
                              During or before your onboarding call, accept all invites so you can start:
                            </p>
                            <div className="space-y-2 mb-4">
                              <div className="flex items-center gap-2 bg-white/5 p-2 rounded-lg">
                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                <span className="text-slate-300 text-sm">Making orders</span>
                              </div>
                              <div className="flex items-center gap-2 bg-white/5 p-2 rounded-lg">
                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                <span className="text-slate-300 text-sm">Creating tasks</span>
                              </div>
                              <div className="flex items-center gap-2 bg-white/5 p-2 rounded-lg">
                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                <span className="text-slate-300 text-sm">Collaborating with your team</span>
                              </div>
                            </div>
                            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                              <p className="text-slate-300 text-xs">
                                <span className="text-yellow-400 font-semibold">Important:</span> Check your email for workspace and platform invites. Accept them all to get full access!
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Step 8: Onboarding Status Check */}
                      {step.id === 8 && (
                        <div className="space-y-4">
                          <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
                            <h4 className="text-white font-semibold mb-3">Has your account rep scheduled your onboarding call?</h4>
                            <div className="space-y-2">
                              <label className="flex items-center gap-3 p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-all">
                                <input type="radio" name="onboardingStatus" className="w-4 h-4 text-emerald-500" />
                                <CheckCircle2 className="w-5 h-5 text-green-400" />
                                <span className="text-slate-300">Yes, scheduled</span>
                              </label>
                              <label className="flex items-center gap-3 p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-all">
                                <input type="radio" name="onboardingStatus" className="w-4 h-4 text-emerald-500" />
                                <Clock className="w-5 h-5 text-yellow-400" />
                                <span className="text-slate-300">No, not yet</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </section>
  );
};

export default StepDetails;
